import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Download from "@mui/icons-material/Download";
import Visibility from "@mui/icons-material/Visibility";
import Refresh from "@mui/icons-material/Refresh";
import OpenInNew from "@mui/icons-material/OpenInNew";
import ContentCopy from "@mui/icons-material/ContentCopy";
import UploadFile from "@mui/icons-material/UploadFile";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Palette from "@mui/icons-material/Palette";
import WarningAmber from "@mui/icons-material/WarningAmber";
import QRCode from "qrcode";
import type {
  BrandingKind,
  Ebook,
  EbookDisplayMode,
  EbookFont,
  EbookPublication,
  Processing,
  PlanEntitlement,
} from "@ebook/contracts";
import { ebookFonts } from "@ebook/contracts";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslate } from "../i18n";
import { api } from "../lib/api-client";
import { multipartUpload, sha256, uploadBrandingAsset } from "../lib/asset-transfer-client";
import { EmptyState, PageError, PageLoading } from "../components/PageState";
import { PdfUploadDialog } from "../components/PdfUploadDialog";
import { SectionHeader } from "../components/SectionHeader";
import { StatusChip } from "../components/StatusChip";
import { toProjectSlug } from "../lib/project-slug";
import { EbookContentPanel } from "../components/EbookContentPanel";
import { EbookTableOfContentsPanel } from "../components/EbookTableOfContentsPanel";
import { ReaderToolbarSettings } from "../components/ReaderToolbarSettings";
import { GlobalFileInput, GlobalInput } from "../components/GlobalInput";

const MAX_SIZE = 250 * 1024 * 1024;
const pdfDownloadNamePattern = /^[\p{L}\p{M}\p{N} ]*$/u;

type BrandingSaveStatus = "idle" | "saving" | "saved" | "error";
type GeneralSaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

const publicationStatusLabels: Record<string, string> = {
  draft: "Draft",
  queued: "Queued",
  publishing: "Publishing",
  published: "Published",
  failed: "Failed",
};

const publicationProgressLabels: Record<EbookPublication["progressStage"], string> = {
  idle: "พร้อมตั้งค่า",
  queued: "รอเริ่มเผยแพร่",
  preparing: "กำลังเตรียมพื้นที่เผยแพร่",
  copying: "กำลังเตรียมไฟล์หลัก",
  building: "กำลังเตรียมหน้าสำหรับอ่าน",
  packaging: "กำลังจัดชุดไฟล์",
  uploading: "กำลังส่งไฟล์",
  verifying: "กำลังตรวจสอบผลลัพธ์",
  complete: "เผยแพร่สำเร็จ",
  failed: "เผยแพร่ไม่สำเร็จ",
};

function isPublicationComplete(publication: EbookPublication | null): boolean {
  return Boolean(
    publication
    && publication.progressStage === "complete"
    && publication.progressPercent >= 100
  );
}

function isPublicationInFlight(publication: EbookPublication | null): boolean {
  return Boolean(
    publication
    && ["queued", "publishing"].includes(publication.status)
    && !isPublicationComplete(publication),
  );
}

const brandingKindLabels: Record<BrandingKind, string> = {
  favicon: "Favicon",
  logo: "Logo",
  background: "Background image",
  cover: "ภาพปก",
};

function PublishedUrlQrCode({
  title,
  description,
  url,
  imageUrl,
  iconUrl,
  regenerateDisabled,
}: {
  title: string;
  description: string;
  url: string;
  imageUrl: string | null;
  iconUrl: string | null;
  regenerateDisabled: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState(imageUrl ?? "");
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const __ = useTranslate();

  useEffect(() => {
    setDisplayImageUrl(imageUrl ?? "");
    setError("");
  }, [imageUrl, url]);

  useEffect(() => {
    if (!imageUrl) void generateLocalQr();
  }, [imageUrl, url]);

  async function drawLogo(canvas: HTMLCanvasElement, logoUrl: string): Promise<void> {
    const context = canvas.getContext("2d");
    if (!context) return;
    const image = new Image();
    image.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("QR logo load failed"));
      image.src = logoUrl;
    });
    const size = canvas.width;
    const circleSize = Math.round(size * 0.248);
    const iconSize = Math.round(size * 0.152);
    const center = size / 2;
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const scale = naturalWidth > 0 && naturalHeight > 0
      ? Math.min(iconSize / naturalWidth, iconSize / naturalHeight)
      : 1;
    const drawWidth = Math.round(naturalWidth * scale);
    const drawHeight = Math.round(naturalHeight * scale);
    context.save();
    context.fillStyle = "#fff";
    context.beginPath();
    context.arc(center, center, circleSize / 2, 0, Math.PI * 2);
    context.fill();
    context.clip();
    context.drawImage(
      image,
      center - drawWidth / 2,
      center - drawHeight / 2,
      drawWidth,
      drawHeight,
    );
    context.restore();
  }

  async function generateLocalQr(): Promise<void> {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setGenerating(true);
    setError("");
    try {
      await QRCode.toCanvas(canvas, url, {
        errorCorrectionLevel: "H",
        margin: 2,
        width: 500,
        color: {
          dark: "#111827",
          light: "#ffffff",
        },
      });
      if (iconUrl) {
        await drawLogo(canvas, iconUrl).catch(() => undefined);
      }
      setDisplayImageUrl(canvas.toDataURL("image/png"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("สร้าง QR code ไม่สำเร็จ"));
    } finally {
      setGenerating(false);
    }
  }

  async function copyUrl(): Promise<void> {
    if (!navigator.clipboard) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return <Box
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      p: { xs: 2, sm: 2.5 },
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
      alignItems: { xs: "center", sm: "flex-start" },
      bgcolor: "background.default",
    }}
  >
    <Box
      sx={{
        width: 236,
        height: 236,
        borderRadius: 2,
        p: 1,
        bgcolor: "#fff",
        boxShadow: (theme) => theme.palette.mode === "dark"
          ? "0 16px 40px rgba(0,0,0,.28)"
          : "0 16px 40px rgba(15,23,42,.08)",
      }}
    >
      {displayImageUrl
        ? <Box
            component="img"
            src={displayImageUrl}
            alt={__("QR code สำหรับ {{title}}", { title })}
            aria-label={__("QR code สำหรับ {{title}}", { title })}
            onError={() => {
              if (displayImageUrl.startsWith("data:")) {
                setError(__("สร้าง QR code ไม่สำเร็จ"));
                return;
              }
              void generateLocalQr();
            }}
            sx={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
          />
        : <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "text.secondary",
              fontSize: 13,
            }}
          >
            {__("กำลังสร้าง QR Code ใหม่")}
          </Box>}
      <Box component="canvas" ref={canvasRef} sx={{ display: "none" }} />
    </Box>
    <Box sx={{ minWidth: 0, textAlign: { xs: "center", sm: "left" } }}>
      <Typography fontWeight={700}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {description}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mt={1.5}
        sx={{ wordBreak: "break-all" }}
      >
        {url}
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: "center", sm: "flex-start" }} sx={{ mt: 2 }}>
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          startIcon={<OpenInNew />}
        >
          {__("เปิด URL")}
        </Button>
        <Button
          type="button"
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void copyUrl();
          }}
        >
          {copied ? __("คัดลอกแล้ว") : __("คัดลอก URL")}
        </Button>
        <Button
          type="button"
          variant="outlined"
          startIcon={<Refresh />}
          disabled={regenerateDisabled || generating}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void generateLocalQr();
          }}
        >
          {generating ? __("กำลังสร้าง QR Code ใหม่") : __("สร้าง QR Code ใหม่")}
        </Button>
      </Stack>
    </Box>
  </Box>;
}

export function EbookDetailPage() {
  const { ebookId } = useParams({ from: "/_app/ebooks/$ebookId" });
  const [ebook, setEbook] = useState<Ebook | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [copyrightText, setCopyrightText] = useState("");
  const [pdfDownloadName, setPdfDownloadName] = useState("");
  const [pdfDownloadNameError, setPdfDownloadNameError] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#fff5d9");
  const [fontFamily, setFontFamily] = useState<EbookFont>("Prompt");
  const [displayMode, setDisplayMode] = useState<EbookDisplayMode>("double");
  const [processing, setProcessing] = useState<Processing | null>(null);
  const [publication, setPublication] = useState<EbookPublication | null>(null);
  const [entitlement, setEntitlement] = useState<PlanEntitlement | null>(null);
  const [projectName, setProjectName] = useState("");
  const [publishingAction, setPublishingAction] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [savingPublication, setSavingPublication] = useState(false);
  const [generalSaveStatus, setGeneralSaveStatus] = useState<GeneralSaveStatus>("idle");
  const [displayModeSaveStatus, setDisplayModeSaveStatus] = useState<BrandingSaveStatus>("idle");
  const [brandingSaveStatus, setBrandingSaveStatus] = useState<BrandingSaveStatus>("idle");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [brandingUpload, setBrandingUpload] = useState<BrandingKind | null>(null);
  const [brandingClear, setBrandingClear] = useState<BrandingKind | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState("");
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const uploadAbort = useRef<AbortController | null>(null);
  const brandingSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const brandingSavePending = useRef(0);
  const brandingSaveFailed = useRef(false);
  const titleSaveTimer = useRef<number | null>(null);
  const descriptionSaveTimer = useRef<number | null>(null);
  const copyrightSaveTimer = useRef<number | null>(null);
  const pdfDownloadNameSaveTimer = useRef<number | null>(null);
  const titleSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const titleRef = useRef("");
  const savedTitleRef = useRef("");
  const descriptionRef = useRef("");
  const savedDescriptionRef = useRef("");
  const copyrightRef = useRef("");
  const savedCopyrightRef = useRef("");
  const pdfDownloadNameRef = useRef("");
  const savedPdfDownloadNameRef = useRef("");
  const navigate = useNavigate();
  const { tab: activeTab } = useSearch({ from: "/_app/ebooks/$ebookId" });
  const __ = useTranslate();

  const load = useCallback(async () => {
    setError("");
    try {
      const [next, nextPublication, nextEntitlement] = await Promise.all([
        api.ebook(ebookId),
        api.publication(ebookId),
        api.entitlement(),
      ]);
      setEbook(next);
      setEntitlement(nextEntitlement);
      setPublication(nextPublication);
      setProjectName(nextPublication.projectName ?? toProjectSlug(next.title));
      titleRef.current = next.title;
      savedTitleRef.current = next.title;
      descriptionRef.current = next.description;
      savedDescriptionRef.current = next.description;
      copyrightRef.current = next.copyrightText;
      savedCopyrightRef.current = next.copyrightText;
      pdfDownloadNameRef.current = next.pdfDownloadName ?? "";
      savedPdfDownloadNameRef.current = next.pdfDownloadName ?? "";
      setTitle(next.title);
      setDescription(next.description);
      setCopyrightText(next.copyrightText);
      setPdfDownloadName(next.pdfDownloadName ?? "");
      setPdfDownloadNameError("");
      setBackgroundColor(next.backgroundColor);
      setFontFamily(next.fontFamily);
      setDisplayMode(next.displayMode);
      if (["queued", "processing", "failed"].includes(next.status)) {
        setProcessing(await api.processing(ebookId));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("โหลด E-book ไม่สำเร็จ"));
    }
  }, [__, ebookId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (
      !publication
      || !isPublicationInFlight(publication)
      || !navigator.onLine
      || document.hidden
    ) return;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.publication(ebookId);
        setPublication(next);
        if (!isPublicationInFlight(next)) {
          window.clearInterval(timer);
        }
      } catch {
        // Preserve the latest valid publication state and retry on the next interval.
      }
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [ebookId, publication]);
  useEffect(() => {
    if (!ebook || !["queued", "processing"].includes(ebook.status) || !navigator.onLine || document.hidden) return;
    const timer = window.setInterval(async () => {
      try {
        const status = await api.processing(ebookId);
        setProcessing(status);
        setEbook((current) => current ? { ...current, status: status.status, pageCount: status.pageCount, processedPages: status.processedPages, processingError: status.error } : current);
        if (status.status === "ready" || status.status === "failed") {
          window.clearInterval(timer);
          await load();
        }
      } catch {
        // Keep the last valid progress; the next interval retries.
      }
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [ebook, ebookId, load]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (
        uploadProgress === null
        && !["pending", "saving", "error"].includes(generalSaveStatus)
      ) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [generalSaveStatus, uploadProgress]);
  useEffect(() => () => {
    uploadAbort.current?.abort();
    if (titleSaveTimer.current) window.clearTimeout(titleSaveTimer.current);
    if (descriptionSaveTimer.current) window.clearTimeout(descriptionSaveTimer.current);
    if (copyrightSaveTimer.current) window.clearTimeout(copyrightSaveTimer.current);
    if (pdfDownloadNameSaveTimer.current) window.clearTimeout(pdfDownloadNameSaveTimer.current);
  }, []);

  function pdfDownloadNameValidation(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.length > 30) return __("ชื่อไฟล์ต้องไม่เกิน 30 ตัวอักษร");
    if (!pdfDownloadNamePattern.test(trimmed)) {
      return __("ใช้ได้เฉพาะตัวอักษร ตัวเลข และเว้นวรรค");
    }
    return "";
  }

  function generalFieldsSaved(pdfError = pdfDownloadNameError): boolean {
    return titleRef.current.trim() === savedTitleRef.current
      && descriptionRef.current.trim() === savedDescriptionRef.current
      && copyrightRef.current.trim() === savedCopyrightRef.current
      && pdfDownloadNameRef.current.trim() === savedPdfDownloadNameRef.current
      && !pdfError;
  }

  function scheduleTitleSave(nextTitle: string, delay = 800) {
    titleRef.current = nextTitle;
    if (titleSaveTimer.current) window.clearTimeout(titleSaveTimer.current);
    const trimmedTitle = nextTitle.trim();
    if (!trimmedTitle) {
      setGeneralSaveStatus("idle");
      return;
    }
    if (trimmedTitle === savedTitleRef.current) {
      setGeneralSaveStatus(generalFieldsSaved() ? "saved" : "pending");
      return;
    }
    setGeneralSaveStatus("pending");
    titleSaveTimer.current = window.setTimeout(() => {
      titleSaveTimer.current = null;
      const titleToSave = titleRef.current.trim();
      if (!titleToSave || titleToSave === savedTitleRef.current) return;
      setError("");
      setGeneralSaveStatus("saving");
      titleSaveQueue.current = titleSaveQueue.current.then(async () => {
        try {
          const updated = await api.updateEbook(ebookId, { title: titleToSave });
          savedTitleRef.current = updated.title;
          setEbook(updated);
          const allSaved = titleRef.current.trim() === updated.title
            && descriptionRef.current.trim() === savedDescriptionRef.current
            && copyrightRef.current.trim() === savedCopyrightRef.current
            && pdfDownloadNameRef.current.trim() === savedPdfDownloadNameRef.current
            && !pdfDownloadNameError;
          setGeneralSaveStatus(allSaved ? "saved" : "pending");
        } catch (caught) {
          setGeneralSaveStatus("error");
          setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
        }
      });
    }, delay);
  }

  function scheduleDescriptionSave(nextDescription: string, delay = 800) {
    descriptionRef.current = nextDescription;
    if (descriptionSaveTimer.current) window.clearTimeout(descriptionSaveTimer.current);
    const trimmedDescription = nextDescription.trim();
    if (trimmedDescription === savedDescriptionRef.current) {
      setGeneralSaveStatus(generalFieldsSaved() ? "saved" : "pending");
      return;
    }
    setGeneralSaveStatus("pending");
    descriptionSaveTimer.current = window.setTimeout(() => {
      descriptionSaveTimer.current = null;
      const descriptionToSave = descriptionRef.current.trim();
      if (descriptionToSave === savedDescriptionRef.current) return;
      setError("");
      setGeneralSaveStatus("saving");
      titleSaveQueue.current = titleSaveQueue.current.then(async () => {
        try {
          const updated = await api.updateEbook(ebookId, { description: descriptionToSave });
          savedDescriptionRef.current = updated.description;
          setEbook(updated);
          const allSaved = titleRef.current.trim() === savedTitleRef.current
            && descriptionRef.current.trim() === updated.description
            && copyrightRef.current.trim() === savedCopyrightRef.current
            && pdfDownloadNameRef.current.trim() === savedPdfDownloadNameRef.current
            && !pdfDownloadNameError;
          setGeneralSaveStatus(allSaved ? "saved" : "pending");
        } catch (caught) {
          setGeneralSaveStatus("error");
          setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
        }
      });
    }, delay);
  }

  function schedulePdfDownloadNameSave(nextName: string, delay = 800) {
    pdfDownloadNameRef.current = nextName;
    if (pdfDownloadNameSaveTimer.current) window.clearTimeout(pdfDownloadNameSaveTimer.current);
    const validation = pdfDownloadNameValidation(nextName);
    setPdfDownloadNameError(validation);
    if (validation) {
      setGeneralSaveStatus("error");
      return;
    }
    const trimmedName = nextName.trim();
    if (trimmedName === savedPdfDownloadNameRef.current) {
      setGeneralSaveStatus(generalFieldsSaved("") ? "saved" : "pending");
      return;
    }
    setGeneralSaveStatus("pending");
    pdfDownloadNameSaveTimer.current = window.setTimeout(() => {
      pdfDownloadNameSaveTimer.current = null;
      const nameToSave = pdfDownloadNameRef.current.trim();
      const nextValidation = pdfDownloadNameValidation(nameToSave);
      if (nextValidation) {
        setPdfDownloadNameError(nextValidation);
        setGeneralSaveStatus("error");
        return;
      }
      if (nameToSave === savedPdfDownloadNameRef.current) return;
      setError("");
      setGeneralSaveStatus("saving");
      titleSaveQueue.current = titleSaveQueue.current.then(async () => {
        try {
          const updated = await api.updateEbook(ebookId, { pdfDownloadName: nameToSave || null });
          savedPdfDownloadNameRef.current = updated.pdfDownloadName ?? "";
          setEbook(updated);
          const allSaved = titleRef.current.trim() === savedTitleRef.current
            && descriptionRef.current.trim() === savedDescriptionRef.current
            && copyrightRef.current.trim() === savedCopyrightRef.current
            && pdfDownloadNameRef.current.trim() === savedPdfDownloadNameRef.current;
          setGeneralSaveStatus(allSaved ? "saved" : "pending");
        } catch (caught) {
          setGeneralSaveStatus("error");
          setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
        }
      });
    }, delay);
  }

  function scheduleCopyrightSave(nextCopyrightText: string, delay = 800) {
    copyrightRef.current = nextCopyrightText;
    if (copyrightSaveTimer.current) window.clearTimeout(copyrightSaveTimer.current);
    const trimmedCopyrightText = nextCopyrightText.trim();
    if (trimmedCopyrightText === savedCopyrightRef.current) {
      setGeneralSaveStatus(generalFieldsSaved() ? "saved" : "pending");
      return;
    }
    setGeneralSaveStatus("pending");
    copyrightSaveTimer.current = window.setTimeout(() => {
      copyrightSaveTimer.current = null;
      const copyrightTextToSave = copyrightRef.current.trim();
      if (copyrightTextToSave === savedCopyrightRef.current) return;
      setError("");
      setGeneralSaveStatus("saving");
      titleSaveQueue.current = titleSaveQueue.current.then(async () => {
        try {
          const updated = await api.updateEbook(ebookId, { copyrightText: copyrightTextToSave });
          savedCopyrightRef.current = updated.copyrightText;
          setEbook(updated);
          const allSaved = titleRef.current.trim() === savedTitleRef.current
            && descriptionRef.current.trim() === savedDescriptionRef.current
            && copyrightRef.current.trim() === updated.copyrightText
            && pdfDownloadNameRef.current.trim() === savedPdfDownloadNameRef.current
            && !pdfDownloadNameError;
          setGeneralSaveStatus(allSaved ? "saved" : "pending");
        } catch (caught) {
          setGeneralSaveStatus("error");
          setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
        }
      });
    }, delay);
  }

  function autoSaveBranding(input: {
    backgroundColor?: string;
    fontFamily?: EbookFont;
  }) {
    if (brandingSavePending.current === 0) brandingSaveFailed.current = false;
    brandingSavePending.current += 1;
    setError("");
    setBrandingSaveStatus("saving");

    brandingSaveQueue.current = brandingSaveQueue.current.then(async () => {
      try {
        const updated = await api.updateEbook(ebookId, input);
        setEbook(updated);
      } catch (caught) {
        brandingSaveFailed.current = true;
        setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
      } finally {
        brandingSavePending.current -= 1;
        if (brandingSavePending.current === 0) {
          setBrandingSaveStatus(brandingSaveFailed.current ? "error" : "saved");
        }
      }
    });
  }

  async function saveDisplayMode(nextMode: EbookDisplayMode) {
    if (!ebook || nextMode === displayMode) return;
    const previousMode = displayMode;
    const previousEbook = ebook;
    setDisplayMode(nextMode);
    setEbook({ ...ebook, displayMode: nextMode });
    setDisplayModeSaveStatus("saving");
    setError("");
    try {
      const updated = await api.updateEbook(ebookId, { displayMode: nextMode });
      setEbook(updated);
      setDisplayMode(updated.displayMode);
      setDisplayModeSaveStatus("saved");
    } catch (caught) {
      setEbook(previousEbook);
      setDisplayMode(previousMode);
      setDisplayModeSaveStatus("error");
      setError(caught instanceof Error ? caught.message : __("บันทึกไม่สำเร็จ"));
    }
  }

  async function savePublication(): Promise<EbookPublication | null> {
    if (
      !ebook
      || !projectName
      || publication?.projectNameLocked
      || isPublicationInFlight(publication)
    ) return null;
    setSavingPublication(true);
    setError("");
    try {
      const saved = await api.updatePublication(ebook.id, projectName);
      setPublication(saved);
      return saved;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("บันทึก Project name ไม่สำเร็จ"));
      return null;
    } finally {
      setSavingPublication(false);
    }
  }

  async function uploadBranding(kind: BrandingKind, file: File) {
    if (!ebook) return;
    setBrandingUpload(kind);
    setError("");
    try {
      const started = await api.startBrandingUpload(ebook.id, kind, {
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });
      await uploadBrandingAsset(file, started);
      const updated = await api.completeBrandingUpload(ebook.id, kind, started.objectKey);
      setEbook(updated);
      setPublication(await api.publication(ebook.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("Upload รูปภาพไม่สำเร็จ"));
    } finally {
      setBrandingUpload(null);
    }
  }

  async function clearBranding(kind: BrandingKind) {
    if (!ebook) return;
    setBrandingClear(kind);
    setError("");
    try {
      const updated = await api.clearBrandingAsset(ebook.id, kind);
      setEbook(updated);
      setPublication(await api.publication(ebook.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("ล้างรูปภาพไม่สำเร็จ"));
    } finally {
      setBrandingClear(null);
    }
  }

  async function download() {
    setDownloading(true);
    setError("");
    try {
      const result = await api.downloadEbook(ebookId);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("Download ไม่สำเร็จ"));
    } finally {
      setDownloading(false);
    }
  }

  async function preview() {
    setPreviewing(true);
    setError("");
    try {
      const { token } = await api.previewToken(ebookId);
      const url = new URL("/preview", window.location.origin);
      url.searchParams.set("ebookId", ebookId);
      url.searchParams.set("token", token);
      window.open(`${url.pathname}${url.search}`, "_blank", "noopener,noreferrer");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("เปิด Preview ไม่สำเร็จ"));
    } finally {
      setPreviewing(false);
    }
  }

  async function upload({
    file,
    autoOcrScan,
  }: {
    file: File;
    autoOcrScan: boolean;
  }): Promise<void> {
    if (!ebook) throw new Error(__("ไม่พบ E-book"));
    if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
      throw new Error(__("กรุณาเลือกไฟล์ PDF"));
    }
    if (file.size > MAX_SIZE) {
      throw new Error(__("ไฟล์ต้องมีขนาดไม่เกิน 250 MB"));
    }
    const abort = new AbortController();
    uploadAbort.current = abort;
    setUploadProgress(0);
    setError("");
    try {
      const digest = await sha256(file);
      const started = await api.startUpload(ebook.id, {
        fileName: file.name,
        contentType: "application/pdf",
        size: file.size,
        sha256: digest,
        autoOcrScan,
      });
      const uploaded = await multipartUpload(file, started, setUploadProgress, abort.signal);
      await api.completeUpload(ebook.id, started.uploadId, uploaded.workerUploadId, uploaded.parts);
      setProcessing({ status: "queued", pageCount: 0, processedPages: 0, progress: 0, error: null });
      await load();
    } catch (caught) {
      if (!abort.signal.aborted) {
        throw caught instanceof Error ? caught : new Error(__("Upload ไม่สำเร็จ"));
      }
      throw new Error(__("ยกเลิกการ Upload แล้ว"));
    } finally {
      uploadAbort.current = null;
      setUploadProgress(null);
    }
  }

  async function retry() {
    await api.retryProcessing(ebookId);
    await load();
  }

  function openDeleteDialog() {
    setDeleteConfirmCode(String(Math.floor(1000 + Math.random() * 9000)));
    setDeleteConfirmInput("");
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    setDeleting(true);
    setError("");
    try {
      await api.deleteEbook(ebookId);
      await navigate({ to: "/ebooks" });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("ลบ E-book ไม่สำเร็จ"));
    } finally {
      setDeleting(false);
    }
  }

  async function queuePublication() {
    if (!ebook || !projectName) return;
    setPublishConfirmOpen(false);
    setPublishingAction(true);
    setError("");
    try {
      if (!publication?.projectName || publicationDirty) {
        const saved = await api.updatePublication(ebook.id, projectName);
        setPublication(saved);
      }
      setPublication(await api.publish(ebook.id));
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("Publish ไม่สำเร็จ"));
    } finally {
      setPublishingAction(false);
    }
  }

  if (error && !ebook) return <PageError message={error} onRetry={() => void load()} />;
  if (!ebook) return <PageLoading />;
  const publicationInFlight = isPublicationInFlight(publication);
  const placeholderPublishAvailable = ebook.status === "draft"
    && ebook.pageCount === 0
    && ebook.processedPages === 0;
  const publishAvailable = ebook.status === "ready" || placeholderPublishAvailable;
  const projectNameDisabled = Boolean(publication?.projectNameLocked || publicationInFlight);
  const publicationDirty = !projectNameDisabled
    && projectName !== (publication?.projectName ?? "");
  const publicationProgressActive = publicationInFlight;
  const publicPublishedUrl = publication?.deploymentUrl ?? null;
  const cloudflarePagesUrl = publication?.originDeploymentUrl ?? null;
  const publicationVersion = publication?.publishedAt ?? null;
  const publicPublishedQrCodeUrl = publicPublishedUrl
    ? `${publicPublishedUrl.replace(/\/$/, "")}/qrcode.png${
      publicationVersion ? `?v=${encodeURIComponent(publicationVersion)}` : ""
    }`
    : "";
  const publishDisabledReason = publishingAction
    ? __("กำลังส่งคำสั่ง Publish")
    : !publishAvailable
      ? __("ต้องรอให้ E-book สถานะ ready ก่อน")
      : !projectName
        ? __("ต้องกรอก Project name ก่อน")
        : "";
  return <Box>
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "60% 40%" }}
      alignItems="center"
      gap={2}
      mb={3}
    >
      <Box minWidth={0}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ overflowWrap: "anywhere" }}
        >
          {ebook.title}
        </Typography>
        <Typography color="text.secondary">{__("แก้ไขข้อมูลและจัดการไฟล์ต้นฉบับ")}</Typography>
      </Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent={{ xs: "flex-start", md: "flex-end" }}
        useFlexGap
        flexWrap="wrap"
      >
        <StatusChip status={ebook.status} />
        <Button
          variant="outlined"
          startIcon={<Visibility />}
          disabled={ebook.status !== "ready" || previewing}
          onClick={() => void preview()}
        >
          {__("Preview")}
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          disabled={ebook.status !== "ready" || downloading}
          onClick={() => void download()}
        >
          {downloading ? __("กำลังสร้าง ZIP...") : __("Download")}
        </Button>
      </Stack>
    </Box>
    {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>{error}</Alert>}
    {processing && <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>{__("การประมวลผล PDF")}</Typography>
          {processing.status === "failed" && <Button startIcon={<Refresh />} onClick={() => void retry()}>{__("Retry")}</Button>}
        </Box>
        <LinearProgress
          variant="determinate"
          value={processing.progress}
          color={processing.status === "failed" ? "error" : "primary"}
          sx={{ mt: 2, height: 8, borderRadius: 8 }}
        />
        <Typography variant="body2" color="text.secondary" mt={1}>
          {processing.processedPages} / {processing.pageCount || "?"} {__("หน้า")} ({processing.progress}%)
        </Typography>
        {processing.error && <Alert severity="error" sx={{ mt: 2 }}>{processing.error}</Alert>}
      </CardContent>
    </Card>}
    <Tabs
      value={activeTab}
      onChange={(_event, tab) => void navigate({
        to: "/ebooks/$ebookId",
        params: { ebookId },
        search: { tab },
        replace: true,
      })}
      variant="scrollable"
      scrollButtons="auto"
      aria-label={__("ส่วนจัดการ E-book")}
      sx={{ mb: 3, borderBottom: 2, borderColor: "divider", minHeight: 44 }}
    >
      <Tab value="general" label={__("ข้อมูลทั่วไป")} />
      <Tab value="content" label={__("Content")} />
      <Tab value="table-of-contents" label={__("สารบัญ")} />
      <Tab value="toolbar" label={__("Reader toolbar")} />
      <Tab value="publish" label={__("Publish")} />
    </Tabs>
    <Card variant="outlined" sx={{ display: activeTab === "general" ? "block" : "none" }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <SectionHeader icon={InfoOutlined} title={__("ข้อมูลพื้นฐาน")} />
        <Stack spacing={2.5}>
          <GlobalInput
            fullWidth
            label={__("Title")}
            value={title}
            onChange={(event) => {
              const nextTitle = event.target.value;
              const previousAutomaticSlug = toProjectSlug(title);
              setTitle(nextTitle);
              titleRef.current = nextTitle;
              setGeneralSaveStatus(
                !nextTitle.trim()
                  ? "idle"
                  : nextTitle.trim() === savedTitleRef.current
                    ? "saved"
                    : "pending",
              );
              if (
                !publication?.projectNameLocked
                && !isPublicationInFlight(publication)
                && (!projectName || projectName === previousAutomaticSlug)
              ) {
                setProjectName(toProjectSlug(nextTitle));
              }
            }}
            onBlur={() => scheduleTitleSave(title, 0)}
            inputProps={{ maxLength: 180 }}
          />
          <GlobalInput
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label={__("รายละเอียดอีบุ๊ก")}
            value={description}
            onChange={(event) => {
              const nextDescription = event.target.value.slice(0, 1000);
              setDescription(nextDescription);
              descriptionRef.current = nextDescription;
              setGeneralSaveStatus(
                nextDescription.trim() === savedDescriptionRef.current
                  ? "saved"
                  : "pending",
              );
            }}
            onBlur={() => scheduleDescriptionSave(description, 0)}
            helperText={__("ใช้เป็น Meta description เมื่อ Preview, Download หรือ Publish")}
            inputProps={{ maxLength: 1000 }}
          />
          <GlobalInput
            fullWidth
            label={__("Copyright")}
            value={copyrightText}
            onChange={(event) => {
              const nextCopyrightText = event.target.value.slice(0, 300);
              setCopyrightText(nextCopyrightText);
              copyrightRef.current = nextCopyrightText;
              setGeneralSaveStatus(
                generalFieldsSaved()
                  ? "saved"
                  : "pending",
              );
            }}
            onBlur={() => scheduleCopyrightSave(copyrightText, 0)}
            helperText={__("แสดงเป็นข้อความเล็กในหน้า Reader ถ้าเว้นว่างจะไม่แสดง")}
            inputProps={{ maxLength: 300 }}
          />
          <GlobalInput
            fullWidth
            label={__("ชื่อไฟล์ดาวน์โหลด PDF")}
            value={pdfDownloadName}
            onChange={(event) => {
              const nextName = event.target.value.slice(0, 30);
              setPdfDownloadName(nextName);
              pdfDownloadNameRef.current = nextName;
              const validation = pdfDownloadNameValidation(nextName);
              setPdfDownloadNameError(validation);
              setGeneralSaveStatus(validation
                ? "error"
                : generalFieldsSaved(validation)
                  ? "saved"
                  : "pending");
            }}
            onBlur={() => schedulePdfDownloadNameSave(pdfDownloadName, 0)}
            error={Boolean(pdfDownloadNameError)}
            helperText={pdfDownloadNameError || __("ไม่เกิน 30 ตัวอักษร ใช้ตัวอักษร ตัวเลข และเว้นวรรคเท่านั้น")}
            inputProps={{ maxLength: 30 }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <GlobalInput
              select
              fullWidth
              label={__("การแสดงผลหน้า")}
              value={displayMode}
              disabled={displayModeSaveStatus === "saving"}
              onChange={(event) => void saveDisplayMode(event.target.value as EbookDisplayMode)}
              helperText={__("ใช้กับ Preview, ZIP และเว็บไซต์ที่ Publish")}
              sx={{ maxWidth: { sm: 320 } }}
            >
              <MenuItem value="single">{__("หน้าเดี่ยว")}</MenuItem>
              <MenuItem value="double">{__("หน้าคู่")}</MenuItem>
            </GlobalInput>
            {displayModeSaveStatus !== "idle" && (
              <Typography
                variant="body2"
                color={displayModeSaveStatus === "error" ? "error" : "text.secondary"}
              >
                {{
                  saving: __("กำลังบันทึก…"),
                  saved: __("บันทึกแล้ว"),
                  error: __("บันทึกไม่สำเร็จ"),
                }[displayModeSaveStatus]}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
    <Card variant="outlined" sx={{ display: activeTab === "general" ? "block" : "none", mt: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <SectionHeader icon={UploadFile} title={__("ไฟล์ต้นฉบับ")} />
        <Box
          sx={{
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography fontWeight={700}>PDF</Typography>
            <Typography variant="body2" color="text.secondary">
              {ebook.status === "ready"
                ? __("{{count}} หน้า พร้อมใช้งาน", { count: ebook.pageCount })
                : __("PDF สูงสุด 250 MB และ 3,000 หน้า")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            disabled={uploadProgress !== null}
            onClick={() => setUploadOpen(true)}
          >
            {ebook.status === "ready" ? __("เปลี่ยน PDF") : __("Upload PDF")}
          </Button>
        </Box>
      </CardContent>
    </Card>
    <Card variant="outlined" sx={{ display: activeTab === "publish" ? "block" : "none" }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>{__("การเผยแพร่")}</Typography>
        <Stack spacing={2}>
          <GlobalInput
            fullWidth
            label={__("Project name")}
            value={projectName}
            disabled={projectNameDisabled}
            onChange={(event) => setProjectName(toProjectSlug(event.target.value))}
            helperText={publication?.projectNameLocked
              ? __("Project name ถูกล็อกถาวรหลัง Publish แล้ว")
              : publicationInFlight
                ? __("ไม่สามารถแก้ Project name ระหว่างกำลัง Publish")
                : __("URL จะเป็น https://ebook.appstartup.co.th/ebook/{{project}}", { project: projectName || "{user_project_name}-ebook" })}
            inputProps={{ maxLength: 58, pattern: "[a-z0-9\\-]+" }}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              disabled={
                savingPublication
                || projectNameDisabled
                || !projectName
                || !publicationDirty
              }
              onClick={() => void savePublication()}
            >
              {savingPublication ? __("กำลังบันทึก...") : __("บันทึก Project name")}
            </Button>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
            <Button
              type="button"
              variant="contained"
              disabled={
                publishingAction
                || !publishAvailable
                || !projectName
              }
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setPublishConfirmOpen(true);
              }}
            >
              {publicationInFlight
                ? __("Publish ใหม่")
                : publication?.status === "failed"
                  ? __("Retry publish")
                  : __("Publish")}
            </Button>
            {publishDisabledReason && (
              <Typography variant="caption" color="text.secondary">
                {publishDisabledReason}
              </Typography>
            )}
            {!publishDisabledReason && placeholderPublishAvailable && (
              <Typography variant="caption" color="text.secondary">
                {__("ระบบจะสร้าง PDF หน้า Under construction ก่อนเผยแพร่")}
              </Typography>
            )}
            <Typography variant="body2">
              {__("สถานะ: {{status}}", { status: __(publicationStatusLabels[publication?.status ?? "draft"] ?? publication?.status ?? "draft") })}
            </Typography>
          </Stack>
          {publicationProgressActive && publication && (
            <Alert
              severity="info"
              icon={false}
              sx={{ width: "100%", "& .MuiAlert-message": { width: "100%" } }}
            >
              <Stack spacing={1} width="100%">
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2">
                    {__(publicationProgressLabels[publication.progressStage])}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {publication.progressPercent}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={publication.progressPercent}
                  sx={{ height: 8, borderRadius: 8 }}
                />
              </Stack>
            </Alert>
          )}
          {publication?.lastError && (
            <Alert severity="error">{publication.lastError}</Alert>
          )}
          {publicPublishedUrl && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <PublishedUrlQrCode
                  title={__("Public Asset URL")}
                  description={__("สแกนหรือเปิด URL ผ่าน Asset Worker")}
                  url={publicPublishedUrl}
                  imageUrl={publicPublishedQrCodeUrl}
                  iconUrl={ebook.logoUrl ?? ebook.faviconUrl}
                  regenerateDisabled={
                    publishingAction
                    || publicationInFlight
                  }
                />
              </Grid>
              {cloudflarePagesUrl && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <PublishedUrlQrCode
                    title={__("Cloudflare Pages URL")}
                    description={__("สแกนหรือเปิด URL จาก Cloudflare Pages")}
                    url={cloudflarePagesUrl}
                    imageUrl={null}
                    iconUrl={ebook.logoUrl ?? ebook.faviconUrl}
                    regenerateDisabled={
                      publishingAction
                      || publicationInFlight
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Stack>
      </CardContent>
    </Card>
    <Card variant="outlined" sx={{ display: activeTab === "general" ? "block" : "none", mt: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <SectionHeader
          icon={Palette}
          title={__("Branding และพื้นหลัง")}
          description={__("Background image ไม่บังคับ หากอัปโหลด ระบบ reader จะแสดงภาพแบบเต็มหน้าจอด้วย cover")}
        />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontWeight={600} mb={1}>{__("ภาพปก")}</Typography>
            <Box
              sx={{
                height: 120,
                mb: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "background.default",
                overflow: "hidden",
              }}
            >
              {ebook.coverImageUrl
                ? <Box component="img" src={ebook.coverImageUrl} alt={__("ตัวอย่างภาพปก")} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <Typography variant="caption" color="text.secondary">{__("ไม่มี")}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button component="label" variant="outlined" disabled={brandingUpload !== null || brandingClear !== null}>
                {__("อัปโหลดภาพปก")}
                <GlobalFileInput
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadBranding("cover", file);
                    event.target.value = "";
                  }}
                />
              </Button>
              <Button
                variant="text"
                color="error"
                disabled={!ebook.coverImageUrl || brandingUpload !== null || brandingClear !== null}
                onClick={() => void clearBranding("cover")}
              >
                {brandingClear === "cover" ? __("กำลังล้าง...") : __("ล้าง")}
              </Button>
            </Stack>
            <Typography display="block" variant="caption" color="text.secondary" mt={1}>
              {__("ใช้เป็น Meta image · PNG, JPG หรือ WebP สูงสุด 8 MB")}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontWeight={600} mb={1}>{__("Favicon")}</Typography>
            <Box
              sx={{
                width: 64,
                height: 64,
                mb: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "background.default",
              }}
            >
              {ebook.faviconUrl
                ? <Box component="img" src={ebook.faviconUrl} alt={__("Favicon preview")} sx={{ maxWidth: 48, maxHeight: 48 }} />
                : <Typography variant="caption" color="text.secondary">{__("ไม่มี")}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button component="label" variant="outlined" disabled={brandingUpload !== null || brandingClear !== null}>
                {__("Upload favicon")}
                <GlobalFileInput
                  accept=".ico,image/png,image/x-icon,image/vnd.microsoft.icon"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadBranding("favicon", file);
                    event.target.value = "";
                  }}
                />
              </Button>
              <Button
                variant="text"
                color="error"
                disabled={!ebook.faviconUrl || brandingUpload !== null || brandingClear !== null}
                onClick={() => void clearBranding("favicon")}
              >
                {brandingClear === "favicon" ? __("กำลังล้าง...") : __("ล้าง")}
              </Button>
            </Stack>
            <Typography display="block" variant="caption" color="text.secondary" mt={1}>{__("PNG หรือ ICO สูงสุด 2 MB")}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontWeight={600} mb={1}>{__("Logo")}</Typography>
            <Box
              sx={{
                height: 120,
                mb: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "background.default",
                overflow: "hidden",
              }}
            >
              {ebook.logoUrl
                ? <Box component="img" src={ebook.logoUrl} alt={__("Logo preview")} sx={{ maxWidth: "90%", maxHeight: 96, objectFit: "contain" }} />
                : <Typography variant="caption" color="text.secondary">{__("ไม่มี")}</Typography>}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button component="label" variant="outlined" disabled={brandingUpload !== null || brandingClear !== null}>
                {__("Upload logo")}
                <GlobalFileInput
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadBranding("logo", file);
                    event.target.value = "";
                  }}
                />
              </Button>
              <Button
                variant="text"
                color="error"
                disabled={!ebook.logoUrl || brandingUpload !== null || brandingClear !== null}
                onClick={() => void clearBranding("logo")}
              >
                {brandingClear === "logo" ? __("กำลังล้าง...") : __("ล้าง")}
              </Button>
            </Stack>
            <Typography display="block" variant="caption" color="text.secondary" mt={1}>{__("PNG, JPG หรือ WebP สูงสุด 5 MB")}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography fontWeight={600} mb={1}>{__("Background image")}</Typography>
            <Box
              sx={{
                height: 120,
                mb: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                backgroundColor,
                backgroundImage: ebook.backgroundImageUrl ? `url("${ebook.backgroundImageUrl}")` : "none",
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
              aria-label={__("Background preview")}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button component="label" variant="outlined" disabled={brandingUpload !== null || brandingClear !== null}>
                {__("Upload background")}
                <GlobalFileInput
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadBranding("background", file);
                    event.target.value = "";
                  }}
                />
              </Button>
              <Button
                variant="text"
                color="error"
                disabled={!ebook.backgroundImageUrl || brandingUpload !== null || brandingClear !== null}
                onClick={() => void clearBranding("background")}
              >
                {brandingClear === "background" ? __("กำลังล้าง...") : __("ล้าง")}
              </Button>
            </Stack>
            <Typography display="block" variant="caption" color="text.secondary" mt={1}>{__("PNG, JPG หรือ WebP สูงสุด 15 MB")}</Typography>
          </Grid>
        </Grid>
        <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing=".08em" mt={4} display="block">
          {__("รูปแบบตัวอักษรและสี")}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" mt={1.5}>
          <GlobalInput
            label={__("Background color")}
            type="color"
            defaultValue={backgroundColor}
            onBlur={(event) => {
              const nextColor = event.currentTarget.value;
              if (nextColor === backgroundColor) return;
              setBackgroundColor(nextColor);
              autoSaveBranding({ backgroundColor: nextColor });
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <Typography variant="body2" color="text.secondary">{backgroundColor}</Typography>
          <GlobalInput
            select
            label={__("Google Font")}
            value={fontFamily}
            onChange={(event) => {
              const nextFont = event.target.value as EbookFont;
              if (nextFont === fontFamily) return;
              setFontFamily(nextFont);
              autoSaveBranding({ fontFamily: nextFont });
            }}
            sx={{ minWidth: 180 }}
          >
            {ebookFonts.map((font) => (
              <MenuItem key={font} value={font}>{font}</MenuItem>
            ))}
          </GlobalInput>
          {brandingUpload && <Typography variant="body2">{__("กำลัง upload {{kind}}...", { kind: __(brandingKindLabels[brandingUpload]) })}</Typography>}
          {brandingClear && <Typography variant="body2">{__("กำลังล้าง {{kind}}...", { kind: __(brandingKindLabels[brandingClear]) })}</Typography>}
          {!brandingUpload && brandingSaveStatus !== "idle" && (
            <Typography
              variant="body2"
              color={brandingSaveStatus === "error" ? "error" : "text.secondary"}
            >
              {{
                saving: __("กำลังบันทึก…"),
                saved: __("บันทึกแล้ว"),
                error: __("บันทึกไม่สำเร็จ"),
              }[brandingSaveStatus]}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
    <Card
      variant="outlined"
      sx={{
        display: activeTab === "general" ? "block" : "none",
        mt: 3,
        borderColor: "error.main",
        bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(211,59,59,.06)" : "rgba(211,59,59,.03)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <SectionHeader
          icon={WarningAmber}
          title={__("โซนอันตราย")}
          description={__("รายการจะถูกซ่อนทันที และไฟล์จะถูกลบถาวรหลัง 7 วัน การกระทำนี้ไม่สามารถย้อนกลับได้")}
          color="error"
        />
        <Button color="error" variant="outlined" startIcon={<DeleteOutline />} onClick={openDeleteDialog}>
          {__("ลบ E-book")}
        </Button>
      </CardContent>
    </Card>
    <Card variant="outlined" sx={{ display: activeTab === "content" ? "block" : "none" }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {ebook.status === "ready"
          ? <EbookContentPanel ebookId={ebook.id} />
          : <EmptyState
              title={__("Content ยังไม่พร้อม")}
              description={__("อัปโหลดและรอประมวลผล PDF ให้เสร็จก่อนจัดการข้อความ")}
            />}
      </CardContent>
    </Card>
    <Card
      variant="outlined"
      sx={{ display: activeTab === "table-of-contents" ? "block" : "none" }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {ebook.status === "ready"
          ? <EbookTableOfContentsPanel
              ebookId={ebook.id}
              pageCount={ebook.pageCount}
            />
          : <EmptyState
              title={__("สารบัญยังไม่พร้อม")}
              description={__("อัปโหลดและรอประมวลผล PDF ให้เสร็จก่อนจัดการสารบัญ")}
            />}
      </CardContent>
    </Card>
    {activeTab === "toolbar" && (
      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <ReaderToolbarSettings ebook={ebook} onChange={setEbook} />
        </CardContent>
      </Card>
    )}
    <PdfUploadDialog
      open={uploadOpen}
      progress={uploadProgress}
      pageLimit={entitlement?.pagesPerBook ?? 3000}
      onClose={() => setUploadOpen(false)}
      onUpload={upload}
    />
    <Dialog
      open={publishConfirmOpen}
      onClose={() => !publishingAction && setPublishConfirmOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {publicationInFlight
          ? __("ยืนยันเริ่มเผยแพร่ใหม่?")
          : publication?.status === "failed"
            ? __("ยืนยันการเผยแพร่ซ้ำ?")
            : __("ยืนยันการเผยแพร่?")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography>
            {publicationInFlight
              ? __("ระบบจะยกเลิกงานเผยแพร่เดิมและเริ่มจัดชุดไฟล์ใหม่")
              : placeholderPublishAvailable
                ? __("ระบบจะสร้าง PDF หน้า Under construction แล้วเผยแพร่เมื่อประมวลผลเสร็จ")
              : __("ระบบจะจัดชุดไฟล์และเผยแพร่ E-book นี้ไปยัง URL สาธารณะ")}
          </Typography>
          {publication?.deploymentUrl && (
            <Typography variant="body2" color="text.secondary">
              {__("การเผยแพร่ครั้งใหม่จะอัปเดตไฟล์บน URL เดิมเมื่อเสร็จสมบูรณ์")}
            </Typography>
          )}
          {projectName && (
            <Alert severity="info">
              {__("Project: {{project}}", { project: projectName })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPublishConfirmOpen(false)} disabled={publishingAction}>
          {__("ยกเลิก")}
        </Button>
        <Button
          variant="contained"
          disabled={publishingAction}
          onClick={() => void queuePublication()}
        >
          {publishingAction ? __("กำลังส่งคำสั่ง Publish") : __("ยืนยันเผยแพร่")}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle>{__("ลบ E-book?")}</DialogTitle>
      <DialogContent>
        <Typography>{__("รายการจะถูกซ่อนทันที และไฟล์จะถูกลบถาวรหลัง 7 วัน")}</Typography>
        <Typography variant="body2" color="text.secondary" mt={2}>
          {__("พิมพ์รหัสด้านล่างเพื่อยืนยันการลบ")}
        </Typography>
        <Typography
          variant="h4"
          fontWeight={800}
          letterSpacing={6}
          textAlign="center"
          my={1.5}
          sx={{ fontFamily: "monospace" }}
        >
          {deleteConfirmCode}
        </Typography>
        <GlobalInput
          fullWidth
          autoFocus
          value={deleteConfirmInput}
          onChange={(event) => setDeleteConfirmInput(event.target.value)}
          placeholder={deleteConfirmCode}
          inputProps={{ inputMode: "numeric", maxLength: 4 }}
          disabled={deleting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>{__("ยกเลิก")}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={deleteConfirmInput.trim() !== deleteConfirmCode || deleting}
          onClick={() => void confirmDelete()}
        >
          {deleting ? __("กำลังลบ...") : __("ลบ")}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
